import React from "react";
import {
  PlusIcon,
  MinusIcon,
  GlobeAltIcon,
  TrophyIcon,
  FireIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const EtTrinityOnboardingUnlockBIC = () => {
  // Author - Atul Pandey
  // First Created - 20 Apr 2026
  // Purpose - Unlock your org to become Best in class
  // Updated - 21 Apr 2026 - Added separate tabs for narrative and sample visualization

  // 1 - State variables
  const [expandedCards, setExpandedCards] = React.useState({});
  const [selectedContributor, setSelectedContributor] = React.useState("u1");
  const [activeTab, setActiveTab] = React.useState("narrative");

  // 2 - Handle ToggleCard function
  const toggleCard = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  // 3 - Mock Universe data for onboarding preview
  const universeDailyCumulativeData = [
    { xValue: 0, xLabel: "01 Apr", dateKey: "2026-04-01", dailyCount: 18, cumulativeCount: 18 },
    { xValue: 1, xLabel: "04 Apr", dateKey: "2026-04-04", dailyCount: 24, cumulativeCount: 42 },
    { xValue: 2, xLabel: "08 Apr", dateKey: "2026-04-08", dailyCount: 22, cumulativeCount: 64 },
    { xValue: 3, xLabel: "12 Apr", dateKey: "2026-04-12", dailyCount: 29, cumulativeCount: 93 },
    { xValue: 4, xLabel: "16 Apr", dateKey: "2026-04-16", dailyCount: 31, cumulativeCount: 124 },
    { xValue: 5, xLabel: "20 Apr", dateKey: "2026-04-20", dailyCount: 37, cumulativeCount: 161 },
  ];

  const universeOrgCompareSeries = [
    {
      orgId: "myOrg",
      label: "My Org",
      isMyOrg: true,
      series: [
        { xValue: 0, xLabel: "01 Apr", dateKey: "2026-04-01", cumulativeCount: 4 },
        { xValue: 1, xLabel: "04 Apr", dateKey: "2026-04-04", cumulativeCount: 9 },
        { xValue: 2, xLabel: "08 Apr", dateKey: "2026-04-08", cumulativeCount: 15 },
        { xValue: 3, xLabel: "12 Apr", dateKey: "2026-04-12", cumulativeCount: 22 },
        { xValue: 4, xLabel: "16 Apr", dateKey: "2026-04-16", cumulativeCount: 30 },
        { xValue: 5, xLabel: "20 Apr", dateKey: "2026-04-20", cumulativeCount: 39 },
      ],
    },
    {
      orgId: "org2",
      label: "Org 2",
      isMyOrg: false,
      series: [
        { xValue: 0, xLabel: "01 Apr", dateKey: "2026-04-01", cumulativeCount: 6 },
        { xValue: 1, xLabel: "04 Apr", dateKey: "2026-04-04", cumulativeCount: 12 },
        { xValue: 2, xLabel: "08 Apr", dateKey: "2026-04-08", cumulativeCount: 18 },
        { xValue: 3, xLabel: "12 Apr", dateKey: "2026-04-12", cumulativeCount: 25 },
        { xValue: 4, xLabel: "16 Apr", dateKey: "2026-04-16", cumulativeCount: 34 },
        { xValue: 5, xLabel: "20 Apr", dateKey: "2026-04-20", cumulativeCount: 46 },
      ],
    },
    {
      orgId: "org3",
      label: "Org 3",
      isMyOrg: false,
      series: [
        { xValue: 0, xLabel: "01 Apr", dateKey: "2026-04-01", cumulativeCount: 5 },
        { xValue: 1, xLabel: "04 Apr", dateKey: "2026-04-04", cumulativeCount: 10 },
        { xValue: 2, xLabel: "08 Apr", dateKey: "2026-04-08", cumulativeCount: 13 },
        { xValue: 3, xLabel: "12 Apr", dateKey: "2026-04-12", cumulativeCount: 19 },
        { xValue: 4, xLabel: "16 Apr", dateKey: "2026-04-16", cumulativeCount: 24 },
        { xValue: 5, xLabel: "20 Apr", dateKey: "2026-04-20", cumulativeCount: 31 },
      ],
    },
  ];

  const myOrgUniverseSeries = [
    { xValue: 0, xLabel: "01 Apr", dateKey: "2026-04-01", cumulativeCount: 4 },
    { xValue: 1, xLabel: "04 Apr", dateKey: "2026-04-04", cumulativeCount: 9 },
    { xValue: 2, xLabel: "08 Apr", dateKey: "2026-04-08", cumulativeCount: 15 },
    { xValue: 3, xLabel: "12 Apr", dateKey: "2026-04-12", cumulativeCount: 22 },
    { xValue: 4, xLabel: "16 Apr", dateKey: "2026-04-16", cumulativeCount: 30 },
    { xValue: 5, xLabel: "20 Apr", dateKey: "2026-04-20", cumulativeCount: 39 },
  ];

  const myOrgContributors = [
    { userId: "u1", userName: "Ava", count: 14 },
    { userId: "u2", userName: "Liam", count: 10 },
    { userId: "u3", userName: "Noah", count: 8 },
    { userId: "u4", userName: "Mia", count: 7 },
  ];

  const myOrgUserSeriesMap = {
    u1: [
      { xValue: 0, dateKey: "2026-04-01", cumulativeCount: 1 },
      { xValue: 1, dateKey: "2026-04-04", cumulativeCount: 3 },
      { xValue: 2, dateKey: "2026-04-08", cumulativeCount: 5 },
      { xValue: 3, dateKey: "2026-04-12", cumulativeCount: 8 },
      { xValue: 4, dateKey: "2026-04-16", cumulativeCount: 11 },
      { xValue: 5, dateKey: "2026-04-20", cumulativeCount: 14 },
    ],
    u2: [
      { xValue: 0, dateKey: "2026-04-01", cumulativeCount: 1 },
      { xValue: 1, dateKey: "2026-04-04", cumulativeCount: 2 },
      { xValue: 2, dateKey: "2026-04-08", cumulativeCount: 4 },
      { xValue: 3, dateKey: "2026-04-12", cumulativeCount: 6 },
      { xValue: 4, dateKey: "2026-04-16", cumulativeCount: 8 },
      { xValue: 5, dateKey: "2026-04-20", cumulativeCount: 10 },
    ],
    u3: [
      { xValue: 0, dateKey: "2026-04-01", cumulativeCount: 1 },
      { xValue: 1, dateKey: "2026-04-04", cumulativeCount: 2 },
      { xValue: 2, dateKey: "2026-04-08", cumulativeCount: 3 },
      { xValue: 3, dateKey: "2026-04-12", cumulativeCount: 5 },
      { xValue: 4, dateKey: "2026-04-16", cumulativeCount: 6 },
      { xValue: 5, dateKey: "2026-04-20", cumulativeCount: 8 },
    ],
    u4: [
      { xValue: 0, dateKey: "2026-04-01", cumulativeCount: 1 },
      { xValue: 1, dateKey: "2026-04-04", cumulativeCount: 2 },
      { xValue: 2, dateKey: "2026-04-08", cumulativeCount: 3 },
      { xValue: 3, dateKey: "2026-04-12", cumulativeCount: 4 },
      { xValue: 4, dateKey: "2026-04-16", cumulativeCount: 6 },
      { xValue: 5, dateKey: "2026-04-20", cumulativeCount: 7 },
    ],
  };

  const selectedContributorMeta =
    myOrgContributors.find((user) => user.userId === selectedContributor) ||
    myOrgContributors[0];

  const selectedContributorSeries =
    myOrgUserSeriesMap[selectedContributor] || [];

  // 4 - Best in class cards
  const unlockBIC = [
    {
      id: "universeBenchmark",
      title: "See the Bigger Arena",
      area: "Universe Benchmarking",
      Icon: GlobeAltIcon,
      detailType: "rich",
      intro:
        "Creating growth cannot be guided only by how good we feel about our own progress. It must also be shaped by how we are evolving in the larger universe of other organizations.",
      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            While your own growth may feel meaningful in isolation, the larger question is whether your organization is truly progressing toward{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              best in class
            </span>{" "}
            relative to the wider universe around you.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            This helps you move beyond internal comfort and see whether others may be{" "}
            <span className="font-semibold text-gray-800">
              catching up, building faster momentum, or moving ahead
            </span>{" "}
            in developing their growth muscles.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            The purpose is not to make you{" "}
            <span className="font-semibold text-red-700 bg-red-50 px-2 rounded-full">
              complacent
            </span>{" "}
            when you are doing well, nor{" "}
            <span className="font-semibold text-red-700 bg-red-50 px-2 rounded-full">
              despondent
            </span>{" "}
            when you are not yet where you want to be.
          </p>
        ),
      ],
      closing:
        "The Universe view helps you see your growth in context so you can keep pursuing excellence with greater clarity and ambition.",
    },
    {
      id: "effortConsistency",
      title: "Measure Real Growth Effort",
      area: "Effort & Consistency",
      Icon: FireIcon,
      detailType: "rich",
      intro:
        "Best in class is rarely built through one time bursts of activity. It is built through visible, repeated, and disciplined effort over time.",
      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            Trinity helps make visible{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              how much effort
            </span>{" "}
            is being invested and how consistently that effort is being sustained across the universe.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            This allows you to see not just outcomes, but the deeper signals of{" "}
            <span className="font-semibold text-gray-800">
              discipline, persistence, and growth intensity
            </span>{" "}
            which often determine who moves ahead over time.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            When you see stronger consistency elsewhere, it becomes a nudge to examine what level of{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              commitment and operating rhythm
            </span>{" "}
            may be needed to step up your own game.
          </p>
        ),
      ],
      closing:
        "Best in class is earned through consistency of effort and not through occasional spikes of visible activity.",
    },
    {
      id: "bestInClassMindset",
      title: "Build a Best in class Mindset",
      area: "Perspective & Aspiration",
      Icon: TrophyIcon,
      detailType: "rich",
      intro:
        "To become best in class, your organization must continuously develop the maturity to compare, learn, and respond without losing confidence or balance.",
      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            Seeing the wider universe should create a healthier{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              competitive awareness
            </span>{" "}
            that keeps your growth journey active and alive.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            If others are doing better, the signal is not to feel smaller. It is to become more{" "}
            <span className="font-semibold text-gray-800">
              investigative, deliberate, and hungry
            </span>{" "}
            about what must evolve further.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            If you are doing better, the signal is not to relax. It is to keep compounding that progress because others may already be building momentum behind you.
          </p>
        ),
      ],
      closing:
        "Best in class begins with the maturity to stay ambitious without arrogance and realistic without losing belief.",
    },
    {
      id: "growthSignals",
      title: "See What to Build Next",
      area: "Growth Signals",
      Icon: ChartBarIcon,
      detailType: "rich",
      intro:
        "The value of the Universe view is not only in showing where you stand, but in helping you understand what growth muscles must be strengthened next.",
      points: [
        () => (
          <p className="text-xs text-gray-600 leading-6">
            As your habit building progresses, the Universe view begins to reveal practical signals about where you may need stronger{" "}
            <span className="font-semibold text-orange-700 bg-orange-50 px-2 rounded-full">
              induction, reflections, conversations, creations, or action discipline
            </span>
            .
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            This turns comparison into a more useful guide for{" "}
            <span className="font-semibold text-gray-800">
              next level decisions
            </span>{" "}
            instead of making it a passive scoreboard.
          </p>
        ),
        () => (
          <p className="text-xs text-gray-600 leading-6">
            Over time, this helps your organization move from simply asking{" "}
            <span className="font-semibold text-gray-800">
              where do we stand
            </span>{" "}
            to asking{" "}
            <span className="font-semibold text-lime-700 bg-lime-50 px-2 rounded-full">
              what must we build next to truly become best in class
            </span>
            .
          </p>
        ),
      ],
      closing:
        "The Universe view turns external comparison into useful growth signals so your organization can keep evolving with greater focus and intent.",
    },
  ];

  return (
    <div>
      <p className="text-sm font-bold text-gray-800">Become the Best in class</p>
      <p className="text-xs text-gray-600 mt-1">
        As you keep investing in the right growth building habits, Trinity helps you begin to see how your organization is moving in the larger universe.
      </p>

      {/* Intro Block */}
      <div className="mt-4 bg-gradient-to-r from-orange-50 via-white to-lime-50 border border-orange-100 rounded p-4">
        <div className="flex items-start">
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white border border-orange-100 shadow-sm">
            <GlobeAltIcon className="h-6 w-6 text-orange-600" />
          </div>

          <div className="ml-3 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-bold text-gray-800">Universe View</p>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 border border-orange-200 text-orange-800 text-[10px] font-semibold">
                Best in class Lens
              </span>
            </div>

            <p className="text-xs text-gray-600 mt-1 leading-6">
              This section helps your users understand why the universe matters and also visualize what starts becoming visible as their growth habits strengthen over time.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 flex space-x-2 bg-gray-50 rounded p-2 w-fit">
        <button
          onClick={() => setActiveTab("narrative")}
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
            activeTab === "narrative"
              ? "bg-orange-600 text-white border-orange-700"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          Why it Matters
        </button>

        <button
          onClick={() => setActiveTab("visualize")}
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
            activeTab === "visualize"
              ? "bg-orange-600 text-white border-orange-700"
              : "bg-white text-gray-700 border-gray-300"
          }`}
        >
          Visualize It
        </button>
      </div>

      {/* Narrative Tab */}
      {activeTab === "narrative" && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-4">
          {unlockBIC.map(
            ({ id, title, area, Icon, detailType, intro, points, closing, desc }) => {
              const isExpanded = !!expandedCards[id];

              return (
                <div
                  key={id}
                  className="bg-gray-50 border border-gray-200 rounded p-4 h-full flex flex-col hover:bg-gray-100"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white border border-orange-100 shadow-sm">
                        <Icon className="h-6 w-6 text-orange-600" />
                      </div>

                      <div className="ml-3">
                        <p className="text-sm font-bold text-gray-800">{title}</p>
                        <p className="text-[11px] font-semibold text-orange-700 mt-1 uppercase tracking-wide">
                          {area}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => toggleCard(id)}
                      className="ml-3 flex-shrink-0 rounded-full border border-gray-200 bg-white p-1 hover:bg-orange-50 hover:border-orange-200 transition"
                    >
                      {isExpanded ? (
                        <MinusIcon className="h-4 w-4 text-orange-600" />
                      ) : (
                        <PlusIcon className="h-4 w-4 text-orange-600" />
                      )}
                    </button>
                  </div>

                  {detailType === "rich" ? (
                    <div className="mt-4 pl-12 flex-1">
                      <p className="text-xs text-gray-700 leading-6">{intro}</p>

                      {isExpanded && (
                        <>
                          <div className="mt-3 space-y-2">
                            {points.map((point, index) => (
                              <div key={index} className="flex items-start">
                                <span className="text-orange-600 text-xs font-bold mr-2 mt-1">
                                  {index + 1}.
                                </span>

                                <div className="flex-1">
                                  {typeof point === "function" ? (
                                    point()
                                  ) : (
                                    <p className="text-xs text-gray-600 leading-6">
                                      {point}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3 bg-white border border-orange-100 rounded p-3 hover:bg-orange-100">
                            <p className="text-xs text-gray-700 leading-6">{closing}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    isExpanded && (
                      <div className="mt-3 pl-12 flex-1">
                        <p className="text-xs text-gray-600 leading-6">{desc}</p>
                      </div>
                    )
                  )}
                </div>
              );
            }
          )}
        </div>
      )}

      {/* Visualize Tab */}
      {activeTab === "visualize" && (
        <div className="mt-5 bg-white border border-gray-200 rounded p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">
                Trinity Universe · <span className="font-bold text-orange-600">Conversations</span>
              </p>
              <p className="text-sm font-bold text-gray-800 mt-1">
                Your Universe Preview
              </p>
            </div>

            <div className="text-right">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-100 border border-orange-200 text-orange-800 text-[10px] font-semibold">
                Sample Visualization
              </span>
            </div>
          </div>

          {/* Top Metrics */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
              <p className="text-xs text-orange-700 uppercase tracking-wide">
                My Org Contribution
              </p>
              <div className="mt-2 flex items-end gap-2">
                <p className="text-3xl font-bold text-orange-800">39</p>
                <p className="text-sm text-orange-600">conversations</p>
              </div>
              <p className="mt-1 text-xs text-orange-700">
                24% of visible universe
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <p className="text-xs text-purple-700 uppercase tracking-wide">
                Universe Rank
              </p>
              <div className="mt-2 flex items-end gap-2">
                <p className="text-3xl font-bold text-purple-800">#2</p>
                <p className="text-sm text-purple-600">of 3</p>
              </div>
              <p className="mt-1 text-xs text-purple-700">
                Ranked by habit driven contribution
              </p>
            </div>

            <div className="bg-lime-50 rounded-lg p-4 border border-lime-100">
              <p className="text-xs text-lime-700 uppercase tracking-wide">
                Universe Activity
              </p>
              <div className="mt-2 flex items-end gap-2">
                <p className="text-3xl font-bold text-lime-800">161</p>
                <p className="text-sm text-lime-600">conversations</p>
              </div>
              <p className="mt-1 text-xs text-lime-700">
                Across visible organizations
              </p>
            </div>
          </div>

          <div className="mt-4 bg-gray-50 rounded-lg p-4 border">
            <p className="text-xs text-gray-700 leading-6">
              This preview is meant to create excitement about what starts becoming visible as users keep investing in the right growth habits. It is not meant to overwhelm the user during onboarding.
            </p>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LEFT SIDE */}
            <div className="bg-white rounded-lg border p-4 space-y-5">
              <div>
                <p className="text-sm font-bold text-gray-800">True Universe View</p>
                <p className="text-xs text-gray-500">
                  How the larger universe is trending and how organizations are contributing to that movement.
                </p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Universe Activity Trend
                </p>

                <div className="h-64">
                  <ResponsiveContainer>
                    <LineChart
                      data={universeDailyCumulativeData}
                      margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="xValue"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(x) =>
                          universeDailyCumulativeData?.[x]?.xLabel || ""
                        }
                      />
                      <YAxis hide />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const p = payload[0]?.payload;

                          return (
                            <div className="bg-white p-2 border rounded shadow text-xs">
                              <p className="font-semibold">{p.dateKey}</p>
                              <p>Daily: {p.dailyCount}</p>
                              <p>Cumulative: {p.cumulativeCount}</p>
                            </div>
                          );
                        }}
                      />
                      <Legend />
                      <Bar
                        dataKey="dailyCount"
                        fill="#84cc16"
                        name="Daily Universe"
                        barSize={18}
                      />
                      <Line
                        type="monotone"
                        dataKey="cumulativeCount"
                        stroke="#EA580C"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        name="Cumulative Universe"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Org Contribution Trend
                </p>

                <div className="h-72">
                  <ResponsiveContainer>
                    <LineChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        dataKey="xValue"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(x) =>
                          universeOrgCompareSeries?.[0]?.series?.[x]?.xLabel || ""
                        }
                      />
                      <YAxis hide />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const p = payload[0]?.payload;

                          return (
                            <div className="bg-white p-2 border rounded shadow text-xs">
                              <p className="font-semibold">{p.dateKey}</p>
                              <p>Cumulative: {p.cumulativeCount}</p>
                            </div>
                          );
                        }}
                      />
                      <Legend />
                      {universeOrgCompareSeries.map((orgSeries, idx) => (
                        <Line
                          key={orgSeries.orgId}
                          data={orgSeries.series}
                          type="monotone"
                          dataKey="cumulativeCount"
                          stroke={
                            orgSeries.isMyOrg
                              ? "#EA580C"
                              : idx === 1
                              ? "#2563EB"
                              : "#16A34A"
                          }
                          strokeWidth={orgSeries.isMyOrg ? 3 : 1.75}
                          dot={false}
                          name={orgSeries.label}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="bg-white rounded-lg border p-4 space-y-5">
              <div>
                <p className="text-sm font-bold text-gray-800">My Org Contributors</p>
                <p className="text-xs text-gray-500">
                  A preview of how your internal contributors and momentum become visible over time.
                </p>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-gray-700">
                    My Org Trend
                  </p>
                  <p className="text-[10px] text-gray-500">
                    Total: <span className="font-bold">39</span>
                  </p>
                </div>

                <div className="h-64 mt-2">
                  <ResponsiveContainer>
                    <LineChart data={myOrgUniverseSeries}>
                      <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                      <XAxis
                        dataKey="xValue"
                        tick={{ fontSize: 10, fill: "#9ca3af" }}
                        interval="preserveStartEnd"
                        tickFormatter={(x) => myOrgUniverseSeries?.[x]?.xLabel || ""}
                      />
                      <YAxis hide />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const p = payload[0]?.payload;

                          return (
                            <div className="bg-white p-2 border rounded shadow text-xs">
                              <p className="font-semibold">{p.dateKey}</p>
                              <p>Cumulative: {p.cumulativeCount}</p>
                            </div>
                          );
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="cumulativeCount"
                        stroke="#EA580C"
                        strokeWidth={2}
                        dot={{
                          r: 2,
                          fill: "#EA580C",
                          stroke: "#fff",
                          strokeWidth: 1,
                        }}
                        activeDot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="rounded-lg border bg-gray-50 p-3">
                <p className="text-xs font-semibold text-gray-700 mb-3">
                  Contributor Momentum
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1 md:col-span-1">
                    {myOrgContributors.map((user, index) => (
                      <button
                        key={user.userId}
                        onClick={() => setSelectedContributor(user.userId)}
                        className={`w-full flex justify-between items-center text-xs p-2 border-b rounded ${
                          selectedContributor === user.userId
                            ? "bg-orange-100 text-orange-800 font-semibold"
                            : "hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-400">
                            {index + 1}
                          </span>
                          <span>{user.userName}</span>
                        </span>

                        <span className="font-bold">{user.count}</span>
                      </button>
                    ))}
                  </div>

                  <div className="h-32 md:col-span-2">
                    <ResponsiveContainer>
                      <LineChart data={selectedContributorSeries}>
                        <CartesianGrid strokeDasharray="3 4" stroke="#d1d5db" />
                        <XAxis hide />
                        <YAxis hide />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0]?.payload;

                            return (
                              <div className="bg-white p-2 border rounded shadow text-xs">
                                <p className="font-semibold">{p.dateKey}</p>
                                <p>Cumulative: {p.cumulativeCount}</p>
                              </div>
                            );
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="cumulativeCount"
                          stroke="#EA580C"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    <p className="mt-1 text-[10px] text-gray-400 text-center">
                      {selectedContributorMeta?.userName} · conversation momentum
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-600 mt-4 leading-6">
            Over time, this view helps your organization understand not just where it stands, but whether its effort and consistency are strong enough to move ahead in the universe.
          </p>
        </div>
      )}
    </div>
  );
};

export default EtTrinityOnboardingUnlockBIC;